import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { DollarSign, TrendingUp, AlertTriangle, CheckCircle } from 'react-feather';
import Card from '../../components/UI/Card';
import CalculatorInput from '../../components/Tools/CalculatorInput';
import Button from '../../components/UI/Button';
import useTranslation from '../../hooks/useTranslation';

const FixFlipROI = () => {
  const [inputs, setInputs] = useState({
    purchasePrice: '',
    rehabCost: '',
    arvValue: '',
    sellingCosts: '',
    holdingCosts: ''
  });

  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();

  const handleInputChange = (field, value) => {
    setInputs(prev => ({
      ...prev,
      [field]: value
    }));
  };


  const calculateROI = () => {
    if (!inputs.purchasePrice || !inputs.rehabCost || !inputs.arvValue) {
      setResults(null);
      return;
    }
    setLoading(true);
    // Calculation logic (from backend)
    const purchasePrice = parseFloat(inputs.purchasePrice) || 0;
    const rehabCost = parseFloat(inputs.rehabCost) || 0;
    const arvValue = parseFloat(inputs.arvValue) || 0;
    const sellingCosts = parseFloat(inputs.sellingCosts) || 0;
    const holdingCosts = parseFloat(inputs.holdingCosts) || 0;

    const totalInvestment = purchasePrice + rehabCost + holdingCosts;
    const profit = arvValue - sellingCosts - totalInvestment;
    const roi = totalInvestment > 0 ? (profit / totalInvestment) * 100 : 0;
    // Optionally, add a deal quality indicator
    const isGoodDeal = roi > 20;
    setResults({ totalInvestment: Math.round(totalInvestment), profit: Math.round(profit), roi: Math.round(roi * 100) / 100, isGoodDeal });
    setLoading(false);
  };

  // Auto-calculate when inputs change
  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      calculateROI();
    }, 500);
    return () => clearTimeout(debounceTimer);
  }, [inputs]);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  return (
    <div className="space-y-6">
      {/* Instructions */}
      <Card className="p-6">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
          {t('fixFlip.instructions.title')}
        </h2>
        <div className="prose dark:prose-invert max-w-none">
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            {t('fixFlip.instructions.intro')}
          </p>
          <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
            <li>{t('fixFlip.instructions.purchase')}</li>
            <li>{t('fixFlip.instructions.rehab')}</li>
            <li>{t('fixFlip.instructions.arv')}</li>
            <li>{t('fixFlip.instructions.selling')}</li>
            <li>{t('fixFlip.instructions.holding')}</li>
          </ul>
        </div>
      </Card>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Input Form */}
        <Card className="p-6">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6 flex items-center space-x-2">
            <DollarSign className="h-5 w-5 text-primary-600" />
            <span>{t('fixFlip.inputs.title')}</span>
          </h2>

          <div className="space-y-4">
            <CalculatorInput
              label={t('fixFlip.input.purchase')}
              value={inputs.purchasePrice}
              onChange={(value) => handleInputChange('purchasePrice', value)}
              placeholder="200000"
              prefix="$"
            />
            <CalculatorInput
              label={t('fixFlip.input.rehab')}
              value={inputs.rehabCost}
              onChange={(value) => handleInputChange('rehabCost', value)}
              placeholder="50000"
              prefix="$"
            />
            <CalculatorInput
              label={t('fixFlip.input.arv')}
              value={inputs.arvValue}
              onChange={(value) => handleInputChange('arvValue', value)}
              placeholder="350000"
              prefix="$"
            />

            <CalculatorInput
              label={t('fixFlip.input.selling')}
              value={inputs.sellingCosts}
              onChange={(value) => handleInputChange('sellingCosts', value)}
              placeholder="25000"
              prefix="$"
            />

            <CalculatorInput
              label={t('fixFlip.input.holding')}
              value={inputs.holdingCosts}
              onChange={(value) => handleInputChange('holdingCosts', value)}
              placeholder="5000"
              prefix="$"
            />
          </div>
        </Card>

        {/* Results */}
        <Card className="p-6">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6 flex items-center space-x-2">
            <TrendingUp className="h-5 w-5 text-primary-600" />
            <span>{t('fixFlip.results.title')}</span>
          </h2>

          {loading && (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary-600 border-t-transparent"></div>
            </div>
          )}

          {results && !loading && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
              className="space-y-4"
            >
              {/* Deal Quality Indicator */}
              <div className={`p-4 rounded-lg border-2 ${
                results.isGoodDeal
                  ? 'bg-green-50 border-green-200 dark:bg-green-900/20 dark:border-green-800'
                  : 'bg-yellow-50 border-yellow-200 dark:bg-yellow-900/20 dark:border-yellow-800'
              }`}>
                <div className="flex items-center space-x-2">
                  {results.isGoodDeal ? (
                    <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400" />
                  ) : (
                    <AlertTriangle className="h-5 w-5 text-yellow-600 dark:text-yellow-400" />
                  )}
                  <span className={`font-medium ${
                    results.isGoodDeal
                      ? 'text-green-800 dark:text-green-200'
                      : 'text-yellow-800 dark:text-yellow-200'
                  }`}>
                    {results.isGoodDeal ? t('fixFlip.results.goodDeal') : t('fixFlip.results.review')}
                  </span>
                </div>
              </div>

              {/* Key Metrics */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gray-50 dark:bg-dark-700 p-4 rounded-lg">
                  <p className="text-sm text-gray-600 dark:text-gray-400">{t('fixFlip.results.totalInvestment')}</p>
                  <p className="text-xl font-bold text-gray-900 dark:text-white">
                    {formatCurrency(results.totalInvestment)}
                  </p>
                </div>

                <div className="bg-gray-50 dark:bg-dark-700 p-4 rounded-lg">
                  <p className="text-sm text-gray-600 dark:text-gray-400">{t('fixFlip.results.netProfit')}</p>
                  <p className={`text-xl font-bold ${
                    results.profit > 0 ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {formatCurrency(results.profit)}
                  </p>
                </div>

                <div className="bg-gray-50 dark:bg-dark-700 p-4 rounded-lg">
                  <p className="text-sm text-gray-600 dark:text-gray-400">{t('fixFlip.results.roiPercentage')}</p>
                  <p className={`text-xl font-bold ${
                    results.roi > 20 ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {results.roi}%
                  </p>
                </div>
              </div>

              {/* Analysis */}
              <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-200 dark:border-blue-800">
                <h3 className="font-semibold text-blue-900 dark:text-blue-100 mb-2">{t('fixFlip.results.analysisTitle')}</h3>
                <div className="text-sm text-blue-800 dark:text-blue-200 space-y-1">
                  <p>{t('fixFlip.results.analysisLine1', `• Target ROI for fix & flip: 20%+ (yours: ${results.roi}%)`, { roi: results.roi })}</p>
                  <p>{t('fixFlip.results.analysisLine2')}</p>
                </div>
              </div>
            </motion.div>
          )}

          {!results && !loading && (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
              {t('fixFlip.results.empty')}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};

export default FixFlipROI;
